import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { auth } from "@/lib/auth";
import { LanderService } from "@gen/tracker/v1/lander_connect";
import { Lander, LanderUsageFilter, RiskStatus, type LanderCampaign, type LanderType } from "@gen/tracker/v1/lander_pb";
import { DateRange, ListSort, Pagination } from "@gen/tracker/v1/common_pb";
import type { StatsRow } from "@gen/tracker/v1/reporting_pb";
import { useByIds } from "@/lib/services/catalog";
import { basisToProto } from "@/lib/services/analytics-service";
import type { ConversionBasis } from "@/lib/period";

const client = createClient(LanderService);

export const landerKeys = {
  all: ["landers"] as const,
  lists: ["landers", "list"] as const,
  list: (params: ListLandersParams) => [...landerKeys.all, "list", params] as const,
  detail: (id: string) => [...landerKeys.all, "detail", id] as const,
};

export interface ListLandersParams {
  search?: string;
  includeArchived?: boolean;
  /** Exactly these landers (useLandersByIds is the reader for names). */
  ids?: string[];
  /** name | updated_at | risk_status | created_at; none = newest first. */
  sort?: ListSort;
  /** Only these page types; none = every type. */
  types?: LanderType[];
  /** Only these languages / countries / risk flags; none = any. */
  languages?: string[];
  countries?: string[];
  risks?: RiskStatus[];
  /** «Используется»: served by a live campaign, or not; none = either. */
  usage?: LanderUsageFilter;
  /** One page; the API's default is 50 and its ceiling 500. */
  limit?: number;
  offset?: number;
}

export function useListLanders(
  params: ListLandersParams = {},
  options?: Partial<UseQueryOptions<{ items: Lander[]; total: number }>>,
) {
  return useQuery({
    queryKey: landerKeys.list(params),
    queryFn: async () => {
      const res = await client.listLanders({
        search: params.search ?? "",
        includeArchived: !!params.includeArchived,
        ids: params.ids ?? [],
        types: params.types ?? [],
        languages: params.languages ?? [],
        countries: params.countries ?? [],
        riskStatuses: params.risks ?? [],
        usage: params.usage ?? LanderUsageFilter.UNSPECIFIED,
        sort: params.sort,
        pagination: new Pagination({
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        }),
      });
      return {
        items: res.items,
        total: res.pagination?.total ?? res.items.length,
      };
    },
    placeholderData: keepPreviousData,
    ...options,
  });
}

/** The landers with these ids, by id — archived ones too, a campaign may still name them. */
export function useLandersByIds(ids: Iterable<string>, enabled = true) {
  return useByIds<Lander>(
    landerKeys.all,
    ids,
    async (chunk) => (await client.listLanders({ ids: chunk, pagination: new Pagination({ limit: chunk.length, offset: 0 }) })).items,
    enabled,
  );
}

/** The library's counts over every lander that is not archived; its traffic is the report engine's. */
export function useLanderLibrary() {
  return useQuery({
    queryKey: [...landerKeys.all, "library"] as const,
    queryFn: async () => client.getLanderLibrary({}),
  });
}

export interface LanderNumbersQuery {
  ids: string[];
  from: string;
  to: string;
  timezone: string;
  basis: ConversionBasis;
  compare?: { from: string; to: string };
  metrics: string[];
}

export interface LanderNumbersResult {
  /** Lander id → the report engine's row for it, its comparison and its live campaigns. */
  rows: Map<string, { stats?: StatsRow; compare?: StatsRow; campaigns: number }>;
  totals?: StatsRow;
  compareTotals?: StatsRow;
}

/**
 * The numbers of the landers on screen over the app's period — the report
 * engine's rows for dimension «landing» (ListLanderNumbers), so a row here is
 * the row of «Отчёты» for the same lander and period.
 */
export function useListLanderNumbers(q: LanderNumbersQuery) {
  return useQuery({
    queryKey: [...landerKeys.all, "numbers", q] as const,
    enabled: q.ids.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async (): Promise<LanderNumbersResult> => {
      const res = await client.listLanderNumbers({
        ids: q.ids,
        dateRange: new DateRange({ from: q.from, to: q.to, timezone: q.timezone }),
        attributionBasis: basisToProto(q.basis),
        compareRange: q.compare ? new DateRange({ from: q.compare.from, to: q.compare.to, timezone: q.timezone }) : undefined,
        metricKeys: q.metrics,
      });
      return {
        rows: new Map(res.items.map((i) => [i.landerId, { stats: i.stats, compare: i.compare, campaigns: i.campaignCount }] as const)),
        totals: res.totals,
        compareTotals: res.compareTotals,
      };
    },
  });
}

/** «Где используется»: the live campaigns that serve the lander, with its share of a managed rotation. */
export function useLanderCampaigns(id: string | undefined) {
  return useQuery({
    queryKey: [...landerKeys.detail(id ?? ""), "campaigns"] as const,
    enabled: !!id,
    queryFn: async (): Promise<LanderCampaign[]> => (await client.listLanderCampaigns({ id: id ?? "" })).items,
  });
}

export function useGetLander(id: string | undefined) {
  return useQuery({
    queryKey: landerKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      if (!id) return undefined;
      const res = await client.getLander({ id });
      return res.lander;
    },
  });
}

export function useCreateLander() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (lander: Lander) => {
      const res = await client.createLander({ lander });
      return res.lander!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: landerKeys.all });
    },
  });
}

export function useUpdateLander() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (lander: Lander) => {
      const res = await client.updateLander({ lander });
      return res.lander!;
    },
    onSuccess: (lander) => {
      void qc.invalidateQueries({ queryKey: landerKeys.all });
      qc.setQueryData(landerKeys.detail(lander.id), lander);
    },
  });
}

// ---------------------------------------------------------------------------
// Asset operations (ZIP upload, list, promote, delete)
//
// These flow through plain HTTP because Connect/protobuf is awkward for
// binary streaming. Routes live at /api/v1/landers/{id}/assets and reuse the
// same JWT auth as Connect calls.

export interface LanderAsset {
  id: string;
  landerId: string;
  version: number;
  r2Key: string;
  sizeBytes: number;
  uncompressedSizeBytes: number;
  checksum: string;
  archiveChecksum: string;
  isCurrent: boolean;
  status: "uploading" | "active" | "archived" | "failed";
  entryCount: number;
  mimeSummary: Record<string, number>;
  uploadedBy: string;
  uploadedAt: string;
  createdAt: string;
  /** The version's description, set at upload or later (PATCH). */
  note: string;
  /** The page's address, for a version captured by link. */
  sourceUrl: string;
}

interface ListAssetsResponse {
  items: LanderAsset[];
  currentId: string;
  // macros: detected `{macro_name}` tokens the worker will substitute at
  // serve time. Empty when the lander has no hosted asset or no allowlisted
  // macros were found inside HTML at upload time.
  macros: string[];
}

export const landerAssetKeys = {
  list: (landerId: string) => ["landers", "detail", landerId, "assets"] as const,
};

async function buildAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  const token = await auth.getAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function fetchJSON<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await buildAuthHeaders();
  const res = await fetch(window.location.origin + path, {
    ...init,
    headers: { ...(init.headers || {}), ...headers },
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = (await res.json()) as { error?: string };
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new Error(`asset request failed (${res.status}): ${msg}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function useListLanderAssets(landerId: string | undefined) {
  return useQuery({
    queryKey: landerId ? landerAssetKeys.list(landerId) : ["landers", "assets-disabled"],
    enabled: !!landerId,
    queryFn: async () => {
      if (!landerId) return { items: [], currentId: "", macros: [] } as ListAssetsResponse;
      return fetchJSON<ListAssetsResponse>(`/api/v1/landers/${landerId}/assets`);
    },
  });
}

export interface UploadLanderAssetVars {
  landerId: string;
  file: File;
  /** The version's description («Новая шапка, таймер акции»). */
  note?: string;
  /** Upload without making it the served version. */
  keepCurrent?: boolean;
  onProgress?: (loaded: number, total: number) => void;
}

// useUploadLanderAsset uses XMLHttpRequest under the hood to surface upload
// progress events — fetch's ReadableStream upload is not supported in every
// browser yet (2026-05-04: Safari still partial), so XHR remains the
// pragmatic choice.
export function useUploadLanderAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ landerId, file, note, keepCurrent, onProgress }: UploadLanderAssetVars) => {
      const headers = await buildAuthHeaders();
      const url = `${window.location.origin}/api/v1/landers/${landerId}/assets`;
      return await new Promise<LanderAsset>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", url, true);
        for (const [k, v] of Object.entries(headers)) {
          xhr.setRequestHeader(k, v);
        }
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
        };
        xhr.onerror = () => reject(new Error("network error"));
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText) as LanderAsset);
            } catch (err) {
              reject(err);
            }
            return;
          }
          let msg = xhr.statusText;
          try {
            const body = JSON.parse(xhr.responseText) as { error?: string };
            if (body?.error) msg = body.error;
          } catch {
            /* ignore */
          }
          reject(new Error(`upload failed (${xhr.status}): ${msg}`));
        };
        const form = new FormData();
        form.append("archive", file, file.name);
        if (note) form.append("note", note);
        if (keepCurrent) form.append("keepCurrent", "true");
        xhr.send(form);
      });
    },
    onSuccess: (asset) => {
      void qc.invalidateQueries({ queryKey: landerAssetKeys.list(asset.landerId) });
      void qc.invalidateQueries({ queryKey: landerKeys.detail(asset.landerId) });
    },
  });
}

export function usePromoteLanderAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ landerId, assetId }: { landerId: string; assetId: string }) => {
      return fetchJSON<LanderAsset>(`/api/v1/landers/${landerId}/assets/${assetId}/promote`, {
        method: "POST",
      });
    },
    onSuccess: (asset) => {
      void qc.invalidateQueries({ queryKey: landerAssetKeys.list(asset.landerId) });
    },
  });
}

// ---------------------------------------------------------------------------

export function useDuplicateLander() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, newName }: { id: string; newName?: string }) => {
      const res = await client.duplicateLander({ id, newName: newName ?? "" });
      return res.lander!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: landerKeys.all });
    },
  });
}

export interface LanderUsage {
  flowCount: number;
  campaignCount: number;
  flowIds: string[];
}

export const landerUsageKeys = {
  for: (id: string) => [...landerKeys.detail(id), "usage"] as const,
};

export function useLanderUsage(id: string | undefined) {
  return useQuery({
    queryKey: id ? landerUsageKeys.for(id) : ["landers", "usage-disabled"],
    enabled: !!id,
    staleTime: 60_000,
    queryFn: async (): Promise<LanderUsage> => {
      if (!id) return { flowCount: 0, campaignCount: 0, flowIds: [] };
      const res = await client.getLanderUsage({ id });
      const u = res.usage;
      return {
        flowCount: u?.flowCount ?? 0,
        campaignCount: u?.campaignCount ?? 0,
        flowIds: u?.flowIds ?? [],
      };
    },
  });
}

export interface TestFireResult {
  // Relative URL — renders in-app via the API host's /lander/{id}/ serve
  // handler. Always present.
  previewUrl: string;
  // Absolute URL on the lander's tracking domain (CF Worker — the exact
  // production serve path). Absent when no tracking domain is configured.
  edgePreviewUrl?: string;
  decision: string;
  macros: Record<string, string>;
}

// useTestFireLander hits the HTTP /api/v1/landers/{id}/test-fire endpoint so
// the Preview & Test tab can render the iframe with macros baked into the
// query string. Read-only — no mutation.
export function useTestFireLander() {
  return useMutation({
    mutationFn: async ({
      landerId,
      macros,
    }: {
      landerId: string;
      macros: Record<string, string>;
    }): Promise<TestFireResult> => {
      return fetchJSON<TestFireResult>(`/api/v1/landers/${landerId}/test-fire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ macros }),
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Download

// downloadLanderAsset streams the original ZIP with auth headers and hands
// it to the browser as a file save.
export async function downloadLanderAsset(landerId: string, assetId: string, version: number) {
  const headers = await buildAuthHeaders();
  const res = await fetch(
    `${window.location.origin}/api/v1/landers/${landerId}/assets/${assetId}/download`,
    { headers },
  );
  if (!res.ok) {
    throw new Error(`download failed (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `lander-${landerId}-v${version}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function useArchiveLander() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.archiveLander({ id });
      return id;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: landerKeys.all });
      // Lists only: a detail or a by-ids map under the same root has no
      // `items` to filter.
      const snapshots = qc.getQueriesData<{ items: Lander[]; total: number }>({
        queryKey: landerKeys.lists,
      });
      for (const [key, data] of snapshots) {
        if (!data) continue;
        qc.setQueryData(key, {
          ...data,
          items: data.items.filter((l) => l.id !== id),
          total: Math.max(0, data.total - 1),
        });
      }
      return { snapshots };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx?.snapshots) return;
      for (const [key, data] of ctx.snapshots) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: landerKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// By link, descriptions, diff (boards LanderCreate, LanderVersions)

export interface ImportReport {
  sourceUrl: string;
  files: number;
  skipped: { url: string; reason: string }[];
}

/** «По ссылке»: the page read from its address becomes the lander's next version. */
export function useImportLanderPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ landerId, url, note }: { landerId: string; url: string; note?: string }) =>
      fetchJSON<{ asset: LanderAsset; report: ImportReport }>(`/api/v1/landers/${landerId}/assets/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, note: note ?? "" }),
      }),
    onSuccess: ({ asset }) => {
      void qc.invalidateQueries({ queryKey: landerAssetKeys.list(asset.landerId) });
      void qc.invalidateQueries({ queryKey: landerKeys.detail(asset.landerId) });
    },
  });
}

/** A version's description. */
export function useSetLanderVersionNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ landerId, assetId, note }: { landerId: string; assetId: string; note: string }) =>
      fetchJSON<LanderAsset>(`/api/v1/landers/${landerId}/assets/${assetId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ note }),
      }),
    onSuccess: (asset) => {
      void qc.invalidateQueries({ queryKey: landerAssetKeys.list(asset.landerId) });
    },
  });
}

export interface DiffLine {
  /** " " both, "-" only the older, "+" only the newer. */
  op: " " | "-" | "+";
  old: number;
  new: number;
  text: string;
}

export interface FileDiff {
  path: string;
  /** added | removed | modified */
  change: string;
  binary: boolean;
  added: number;
  removed: number;
  tooLarge?: boolean;
  hunks?: { lines: DiffLine[] }[];
}

export interface VersionDiff {
  from: number;
  to: number;
  added: number;
  removed: number;
  files: FileDiff[];
  unchanged: number;
}

/** What changed between two versions of one lander (server-side, line by line). */
export function useLanderVersionDiff(landerId: string | undefined, fromId: string | undefined, toId: string | undefined) {
  return useQuery({
    queryKey: ["landers", "detail", landerId ?? "", "diff", fromId ?? "", toId ?? ""] as const,
    enabled: !!landerId && !!fromId && !!toId && fromId !== toId,
    staleTime: Infinity, // versions are immutable
    queryFn: async () => fetchJSON<VersionDiff>(`/api/v1/landers/${landerId}/assets/diff?from=${fromId}&to=${toId}`),
  });
}
